/**
 * Dashboard API Library
 *
 * Centralized API functions for Bond Palantir Dashboard v3
 * Handles all HTTP requests with consistent headers and error handling
 */

import config from '../../../config';

const apiBaseUrl = config.api.dashboardApi;

/**
 * Create headers for API requests
 * @param {string} token - JWT token
 * @returns {Object} Headers object
 */
const createHeaders = (token) => ({
    'Authorization': `Bearer ${token}`,
    'Accept': 'application/json',
});

/**
 * Handle API response
 * @param {Response} response - Fetch response
 * @returns {Promise} Parsed JSON response
 * @throws {Error} If response is not ok
 */
const handleResponse = async (response) => {
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
};

// =============================================================================
// User API
// =============================================================================

/**
 * Search users by query
 * @param {string} token - JWT token
 * @param {string} query - Search query
 * @returns {Promise<Object>} User search results
 */
export const searchUsers = async (token, query) => {
    const queryString = `?query=${encodeURIComponent(query)}`;
    const response = await fetch(`${apiBaseUrl}/users/${queryString}`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get user by ID
 * @param {string} token - JWT token
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User data
 */
export const getUserById = async (token, userId) => {
    const response = await fetch(`${apiBaseUrl}/users/${userId}/`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get the homes a user belongs to
 * @param {string} token - JWT token
 * @param {string} userId - User ID
 * @returns {Promise<Object>} { count, results: [{id, name, home_code, address, role, member_count, bond_count, created}] }
 */
export const getUserHomes = async (token, userId) => {
    const response = await fetch(`${apiBaseUrl}/users/${userId}/homes/`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get a home's detail (metadata + members + pending invites + bonds)
 * @param {string} token - JWT token
 * @param {string} homeId - Home ID
 * @returns {Promise<Object>} Home detail
 */
export const getHome = async (token, homeId) => {
    const response = await fetch(`${apiBaseUrl}/homes/${homeId}/`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

// =============================================================================
// Bond API
// =============================================================================

/**
 * Search bonds by query
 * @param {string} token - JWT token
 * @param {string} query - Search query
 * @returns {Promise<Object>} Bond search results
 */
export const searchBonds = async (token, query) => {
    const queryString = `?query=${encodeURIComponent(query)}`;
    const response = await fetch(`${apiBaseUrl}/bonds/${queryString}`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get bond by ID
 * @param {string} token - JWT token
 * @param {string} bondId - Bond ID
 * @returns {Promise<Object>} Bond data
 */
export const getBondById = async (token, bondId) => {
    const response = await fetch(`${apiBaseUrl}/bonds/${bondId}/`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get commands for a specific device (lazy-loaded when user expands the Commands section)
 * @param {string} token - JWT token
 * @param {string} bondId - Bond ID
 * @param {string} deviceId - Device ID (Bond's device_id UUID)
 * @returns {Promise<Array>} List of command objects
 */
export const getDeviceCommands = async (token, bondId, deviceId) => {
    const response = await fetch(`${apiBaseUrl}/bonds/${bondId}/devices/${deviceId}/commands/`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get bond connectivity status
 * @param {string} token - JWT token
 * @param {string} bondId - Bond ID
 * @returns {Promise<Object>} Bond status
 */
export const getBondStatus = async (token, bondId) => {
    const response = await fetch(`${apiBaseUrl}/bonds/${bondId}/status/`, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Get bond events with optional filters
 * @param {string} token - JWT token
 * @param {string} bondId - Bond ID
 * @param {Object} options - Optional filters
 * @param {string} options.start - Start date (ISO format)
 * @param {string} options.end - End date (ISO format)
 * @param {number} options.limit - Max number of events
 * @returns {Promise<Object>} Bond events
 */
export const getBondEvents = async (token, bondId, options = {}) => {
    const params = new URLSearchParams();

    if (options.start) {
        params.append('start_datetime', options.start);
    }
    if (options.end) {
        params.append('end_datetime', options.end);
    }
    if (options.limit) {
        params.append('limit', options.limit);
    }

    const queryString = params.toString();
    const url = queryString
        ? `${apiBaseUrl}/dashboard/logs/${bondId}/?${queryString}`
        : `${apiBaseUrl}/dashboard/logs/${bondId}/`;

    const response = await fetch(url, {
        headers: createHeaders(token),
    });
    return handleResponse(response);
};

/**
 * Create WebSocket URL for live bond events
 * @param {string} bondId - Bond ID
 * @param {string} token - JWT token
 * @returns {string} WebSocket URL
 */
export const getBondEventsWebSocketUrl = (bondId, token) => {
    return config.websocket.getDashboardBondEventsUrl(bondId, token);
};

/**
 * Get user events from CloudWatch (bond-vault-user log group)
 * @param {string} token - JWT token
 * @param {string} userId - User ID (UUID)
 * @param {Object} options - Optional filters
 * @param {string} options.start - Start date (ISO format)
 * @param {string} options.end - End date (ISO format)
 * @param {number} options.limit - Max number of events
 * @returns {Promise<Object>} User events
 */
export const getUserEvents = async (token, userId, options = {}) => {
    const params = new URLSearchParams();

    if (options.start) params.append('start_datetime', options.start);
    if (options.end) params.append('end_datetime', options.end);
    if (options.limit) params.append('limit', options.limit);

    const queryString = params.toString();
    const url = queryString
        ? `${apiBaseUrl}/dashboard/user-logs/${userId}/?${queryString}`
        : `${apiBaseUrl}/dashboard/user-logs/${userId}/`;

    const response = await fetch(url, { headers: createHeaders(token) });
    return handleResponse(response);
};

/**
 * Create WebSocket URL for live user events
 * @param {string} userId - User ID (UUID)
 * @returns {string} WebSocket URL
 */
export const getUserEventsWebSocketUrl = (userId) => {
    return config.websocket.getDashboardUserEventsUrl(userId);
};

/**
 * Get action history for a user (from Redis, key: USER:{userId}:ACTIONS)
 * @param {string} token - JWT token
 * @param {string} userId - User ID
 * @param {Object} options - Optional filters
 * @param {number} options.page_number - Page number (default 1)
 * @returns {Promise<Object>} User action history {count, next, previous, results}
 */
export const getUserActionsHistory = async (token, userId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page_number) params.append('page_number', options.page_number);
    const queryString = params.toString();
    const url = queryString
        ? `${apiBaseUrl}/users/${userId}/actions_history/?${queryString}`
        : `${apiBaseUrl}/users/${userId}/actions_history/`;
    const response = await fetch(url, { headers: createHeaders(token) });
    return handleResponse(response);
};

/**
 * Get v2 action history for a user (from the bond-action-history DynamoDB
 * table: actions on the user's owned + home bonds, merged newest-first)
 * @param {string} token - JWT token
 * @param {string} userId - User ID
 * @param {Object} options - Optional filters
 * @param {number} options.page_number - Page number (default 1)
 * @param {string} options.home_id - Restrict the query to this home's bonds;
 *   omit for every bond the user has
 * @returns {Promise<Object>} User action history {count, next, previous, results}
 */
export const getUserActionsHistoryV2 = async (token, userId, options = {}) => {
    const params = new URLSearchParams();
    if (options.page_number) params.append('page_number', options.page_number);
    if (options.home_id) params.append('home_id', options.home_id);
    const queryString = params.toString();
    const url = queryString
        ? `${apiBaseUrl}/users/${userId}/actions_history_v2/?${queryString}`
        : `${apiBaseUrl}/users/${userId}/actions_history_v2/`;
    const response = await fetch(url, { headers: createHeaders(token) });
    return handleResponse(response);
};

// =============================================================================
// Batch Operations
// =============================================================================

/**
 * Fetch multiple bonds in parallel
 * @param {string} token - JWT token
 * @param {Array<string>} bondIds - Array of bond IDs
 * @returns {Promise<Object>} Map of bondId -> bond data
 */
export const fetchBondsBatch = async (token, bondIds) => {
    const promises = bondIds.map(bondId =>
        getBondById(token, bondId)
            .then(data => ({ bondId, data, error: null }))
            .catch(error => ({ bondId, data: null, error: error.message }))
    );

    const results = await Promise.all(promises);

    const bondsData = {};
    const bondsError = {};

    results.forEach(({ bondId, data, error }) => {
        if (error) {
            bondsError[bondId] = error;
        } else {
            bondsData[bondId] = data;
        }
    });

    return { bondsData, bondsError };
};

/**
 * Poll bond status for multiple bonds
 * @param {string} token - JWT token
 * @param {Array<string>} bondIds - Array of bond IDs
 * @returns {Promise<Object>} Map of bondId -> status
 */
export const pollBondStatusBatch = async (token, bondIds) => {
    const promises = bondIds.map(bondId =>
        getBondStatus(token, bondId)
            .then(status => ({ bondId, status }))
            .catch(error => ({ bondId, status: null, error: error.message }))
    );

    const results = await Promise.all(promises);

    const statusMap = {};
    results.forEach(({ bondId, status }) => {
        if (status) {
            statusMap[bondId] = status;
        }
    });

    return statusMap;
};

/**
 * Fetch customization profile for a bond from the portal API proxy
 * @param {string} token - JWT token
 * @param {string} bondId - Bond ID
 * @returns {Promise<Object|null>} Profile data or null if not found
 */
export const fetchCustomizationProfile = async (token, bondId) => {
    const response = await fetch(`${apiBaseUrl}/bonds/${bondId}/customization_profile/`, {
        headers: createHeaders(token),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
};

export default {
    // User API
    searchUsers,
    getUserById,
    getUserHomes,
    getHome,

    // Bond API
    searchBonds,
    getBondById,
    getBondStatus,
    getBondEvents,
    getBondEventsWebSocketUrl,
    getDeviceCommands,
    getUserActionsHistory,

    // Batch Operations
    fetchBondsBatch,
    pollBondStatusBatch,

    // Portal
    fetchCustomizationProfile,
};
